import React, { useState } from "react";
import clsx from "clsx";
import { Disclosure, Switch, Combobox } from "@headlessui/react";
import { Info, ChevronDown } from "lucide-react";
import ClientInfoToggles from "./ui/ClientInfoToggles";
import BookingLimits from "./ui/BookingLimits";
import {
  timeStrToISO_abstract,
  isoToHHMM_abstract,
  defaultTimezone,
} from "../utils/datetime";
import { CURRENCIES } from "../utils/currency";
import { TIMEZONES_WITH_LABEL } from "../utils/timezones";
import Required from "../utils/Required";
/* ⬇︎ Validation helpers for error display */
import { err, clsErr } from "./validation";
import { v4 as uuid } from "uuid";

export default function SharedSettings({
  value,
  onChange,
  modes = [],
  timezone = defaultTimezone,
  errors = {},
}) {
  const upd = (patch) => onChange(patch);
  const isAccommodation = modes.includes("accommodation");
  const tz = timezone;

  /* ----- timezone combobox search state ------------------------ */
  const [query, setQuery] = useState("");
  const filteredTimezones =
    query === ""
      ? TIMEZONES_WITH_LABEL
      : TIMEZONES_WITH_LABEL.filter((t) =>
          t.label.toLowerCase().includes(query.toLowerCase())
        );

  /* ============================================================= */
  return (
    <div className="space-y-4">
      {/* ????????? Collapsible sections (unchanged) ???????????????????????????????????????????????????????????? */}
      {[
        {
          title: "Client Info",
          body: (
            <ClientInfoToggles
              value={value.client_fields}
              onChange={(v) => upd({ client_fields: v })}
            />
          ),
        },
        {
          title: "Booking Limits",
          body: (
            <BookingLimits
              value={value.booking_limits}
              onChange={(v) => upd({ booking_limits: v })}
              errors={errors}
            />
          ),
        },
      ].map(({ title, body }) => (
        <Disclosure key={title} defaultOpen={title === "General"}>
          {({ open }) => (
            <div className="rounded-xl border bg-white">
              <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3">
                <span className="font-medium">{title}</span>
                <ChevronDown
                  className={clsx(
                    "h-5 w-5 transition-transform",
                    open && "rotate-180"
                  )}
                />
              </Disclosure.Button>
              <Disclosure.Panel className="border-t px-4 py-4 space-y-6">
                {body}
              </Disclosure.Panel>
            </div>
          )}
        </Disclosure>
      ))}

      {/* ????????? General settings header ??????????????????????????????????????????????????????????????????????????????????????? */}
      <h3 className="text-lg font-semibold flex items-center gap-1">
        <Info className="w-4 h-4 text-blue-600" />
        General settings
      </h3>

      {/* ????????? Time???zone picker ????????????????????????????????????????????????????????????????????????????????????????????????????????? */}
      <label className="block text-sm font-medium mb-1">
        Timezone <Required />
      </label>
      <Combobox
        value={value.timezone || null}
        onChange={(tzVal) => upd({ timezone: tzVal })}
      >
        <div className="relative">
          <Combobox.Input
            className={clsx(
              "w-full rounded-lg border-gray-300 shadow-sm focus:ring-blue-600 focus:border-blue-600",
              clsErr(errors, "timezone")
            )}
            displayValue={(tzVal) =>
              TIMEZONES_WITH_LABEL.find((t) => t.value === tzVal)?.label || ""
            }
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search timezone???"
          />
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg">
            {filteredTimezones.length === 0 && (
              <div className="cursor-default select-none py-2 px-4 text-gray-700">
                Nothing found
              </div>
            )}
            {filteredTimezones.map((tzOpt) => (
              <Combobox.Option
                key={tzOpt.value}
                value={tzOpt.value}
                className={({ active }) =>
                  `cursor-pointer select-none px-3 py-1 text-sm ${
                    active ? "bg-blue-600 text-white" : "text-gray-900"
                  }`
                }
              >
                {tzOpt.label}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </div>
      </Combobox>
      {err(errors, "timezone") && (
        <p className="text-xs text-red-500 mt-1">{err(errors, "timezone")}</p>
      )}

      {/* ????????? Currency selector ?????????????????????????????????????????????????????????????????????????????????????????????????????? */}
      <label className="block text-sm font-medium mt-4">
        Base currency <Required />
        <select
          value={value.base_currency ?? ""}
          onChange={(e) => upd({ base_currency: e.target.value })}
          className={clsx(
            "mt-1 block w-full rounded-lg border-gray-300",
            clsErr(errors, "base_currency")
          )}
        >
          <option value="">Select currency???</option>
          {CURRENCIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.code} ??? {c.symbol}
            </option>
          ))}
        </select>
      </label>
      {err(errors, "base_currency") && (
        <p className="text-xs text-red-500 mt-1">
          {err(errors, "base_currency")}
        </p>
      )}

      {modes.includes("table_inventory") && (
        <div className="flex items-center justify-between">
          <span className="text-sm">Allow clients to order beforehand</span>
          <Switch
            checked={value.allow_preorder || false}
            onChange={(c) => upd({ allow_preorder: c })}
            className={clsx(
              value.allow_preorder ? "bg-blue-600" : "bg-gray-200",
              "relative inline-flex h-6 w-11 items-center rounded-full transition"
            )}
          >
            <span
              className={clsx(
                value.allow_preorder ? "translate-x-6" : "translate-x-1",
                "inline-block h-4 w-4 transform rounded-full bg-white transition"
              )}
            />
          </Switch>
        </div>
      )}

      {/* Waitlist toggle removed — waitlist opt-in is always available for clients */}

  {/* Cancellation Policy */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center justify-between">
          <span className="text-sm">Allow reservation notes</span>
          <Switch
            checked={value.allow_notes || false}
            onChange={(c) => upd({ allow_notes: c })}
            className={clsx(
              value.allow_notes ? "bg-blue-600" : "bg-gray-200",
              "relative inline-flex h-6 w-11 items-center rounded-full transition"
            )}
          >
            <span
              className={clsx(
                value.allow_notes ? "translate-x-6" : "translate-x-1",
                "inline-block h-4 w-4 transform rounded-full bg-white transition"
              )}
            />
          </Switch>
        </div>

        {/* booking_lead_days */}
        <label className="text-sm font-medium">
          Booking lead time (days) <Required />
          <input
            type="number"
            min="0"
            max="365"
            value={value.booking_lead_days ?? ""}
            onChange={(e) => {
              const v = Number(e.target.value);
              const clamped = Math.max(0, Math.min(365, isNaN(v) ? 0 : v));
              upd({ booking_lead_days: clamped });
            }}
            className={clsx(
              "mt-1 block w-full rounded-lg border-gray-300 shadow-sm",
              clsErr(errors, "booking_lead_days")
            )}
          />
          <span className="text-[11px] text-gray-500">Max 365 days</span>
          {err(errors, "booking_lead_days") && (
            <span className="text-xs text-red-500 mt-1 block">
              {err(errors, "booking_lead_days")}
            </span>
          )}
        </label>

        {/* ??????????????? Accommodation???specific settings ??????????????? */}
        {isAccommodation && (
          <>
            <h3 className="col-span-3 text-lg font-semibold flex items-center gap-1 mt-4">
              Check???in / Check???out
            </h3>

            <div className="col-span-3 grid grid-cols-2 gap-4">
              {/* Check???in after */}
              <label className="text-sm font-medium text-gray-700">
                Check???in after <Required />
                <input
                  type="time"
                  value={isoToHHMM_abstract(value.accommodation?.check_in_after)}
                  onChange={(e) =>
                    upd({
                      accommodation: {
                        ...value.accommodation,
                        check_in_after: timeStrToISO_abstract(e.target.value),
                      },
                    })
                  }
                  className={clsx(
                    "mt-1 block w-full rounded-lg border-gray-300 shadow-sm",
                    clsErr(errors, "accommodation.check_in_after")
                  )}
                />
                {err(errors, "accommodation.check_in_after") && (
                  <p className="text-xs text-red-500 mt-1">
                    {err(errors, "accommodation.check_in_after")}
                  </p>
                )}
              </label>

              {/* Check???out before */}
              <label className="text-sm font-medium text-gray-700">
                Check???out before <Required />
                <input
                  type="time"
                  value={isoToHHMM_abstract(value.accommodation?.check_out_before)}
                  onChange={(e) =>
                    upd({
                      accommodation: {
                        ...value.accommodation,
                        check_out_before: timeStrToISO_abstract(e.target.value),
                      },
                    })
                  }
                  className={clsx(
                    "mt-1 block w-full rounded-lg border-gray-300 shadow-sm",
                    clsErr(errors, "accommodation.check_out_before")
                  )}
                />
                {err(errors, "accommodation.check_out_before") && (
                  <p className="text-xs text-red-500 mt-1">
                    {err(errors, "accommodation.check_out_before")}
                  </p>
                )}
              </label>
            </div>
          </>
        )}
      </div>

      {/* Manual approval per offering */}
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div className="rounded-xl border bg-white">
            <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3">
              <span className="font-medium">Manual approval</span>
              <ChevronDown className={clsx("h-5 w-5 transition-transform", open && "rotate-180")} />
            </Disclosure.Button>
            <Disclosure.Panel className="border-t px-4 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm">Require manual approval</span>
                <Switch
                  checked={value.requires_manual_approval || false}
                  onChange={(c) => upd({ requires_manual_approval: c })}
                  className={clsx(
                    value.requires_manual_approval ? "bg-blue-600" : "bg-gray-200",
                    "relative inline-flex h-6 w-11 items-center rounded-full transition"
                  )}
                >
                  <span className={clsx(
                    value.requires_manual_approval ? "translate-x-6" : "translate-x-1",
                    "inline-block h-4 w-4 transform rounded-full bg-white transition"
                  )} />
                </Switch>
              </div>

              {value.requires_manual_approval && (
                <ManualApprovalPicker modes={modes} value={value} onChange={onChange} errors={errors} />
              )}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>

      {/* Booking rules per offering (cutoff & lead-time) */}
      <Disclosure defaultOpen={false}>
        {({ open }) => (
          <div className="rounded-xl border bg-white">
            <Disclosure.Button className="flex w-full items-center justify-between px-4 py-3">
              <span className="font-medium">Booking rules (by offering)</span>
              <ChevronDown className={clsx("h-5 w-5 transition-transform", open && "rotate-180")} />
            </Disclosure.Button>
            <Disclosure.Panel className="border-t px-4 py-4 space-y-4">
              <BookingRulesEditor value={value.booking_rules || []} modes={modes} modeData={value} onChange={(rules) => upd({ booking_rules: rules })} errors={errors} />
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */
function buildModeItems(modeData = {}) {
  return {
    personal_services: (modeData.personal_services?.services ?? []).map((s) => ({ id: s.id, name: s.name })),
    table_inventory: (modeData.table_inventory?.services ?? []).map((service) => ({ id: service.id, name: service.name || "Dining service" })),
    class_based: (modeData.class_based?.classes ?? []).map((c) => ({ id: c.id, name: c.title })),
    accommodation: (modeData.accommodation?.room_types ?? []).map((r, idx) => ({ id: r.id ?? `room_${idx}`, name: r.name })),
  };
}

function ManualApprovalPicker({ modes = [], value, onChange, errors }) {
  const settings = value.manual_approval_settings || { apply_to_all: true, selected_offerings: {} };
  const modeItems = buildModeItems(value);
  const applyAll = settings.apply_to_all !== false;
  const setSettings = (patch) => onChange({ manual_approval_settings: { ...settings, ...patch } });

  return (
    <div className="space-y-4">
      <label className="text-sm flex items-center gap-2">
        <input
          type="radio"
          name="ma_scope"
          checked={applyAll}
          onChange={() => setSettings({ apply_to_all: true })}
        />
        Apply to all offerings
      </label>
      <label className="text-sm flex items-center gap-2">
        <input
          type="radio"
          name="ma_scope"
          checked={!applyAll}
          onChange={() => setSettings({ apply_to_all: false })}
        />
        Custom selection
      </label>

      {!applyAll && (
        <div className={clsx("rounded border p-3", clsErr(errors, "manual_approval_settings.selected_offerings"))}>
          {Object.entries(modeItems).map(([mode, items]) =>
            items.length === 0 ? null : (
              <div key={mode} className="mb-3">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold">{mode.replace("_", " ")}</h4>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings({
                        selected_offerings: {
                          ...(settings.selected_offerings || {}),
                          [mode]: items.map((it) => it.id),
                        },
                      })
                    }
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Select All
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {items.map(({ id, name }, idx) => {
                    const sel = settings.selected_offerings?.[mode] || [];
                    const checked = sel.includes(id);
                    return (
                      <label key={id} className="text-xs flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? sel.filter((x) => x !== id) : [...sel, id];
                            setSettings({
                              selected_offerings: {
                                ...(settings.selected_offerings || {}),
                                [mode]: next,
                              },
                            });
                          }}
                        />
                        {name || `Item ${idx + 1}`}
                      </label>
                    );
                  })}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {err(errors, "manual_approval_settings.selected_offerings") && (
        <p className="text-xs text-red-500 mt-1">{err(errors, "manual_approval_settings.selected_offerings")}</p>
      )}
    </div>
  );
}

function BookingRulesEditor({ value = [], modes = [], modeData = {}, onChange, errors = {} }) {
  const rules = value;
  const modeItems = buildModeItems(modeData);
  const add = () =>
    onChange([
      ...rules,
      {
        id: uuid(),
        label: "",
        cutoff_min: 0,
        lead_days: 0,
        scope: "all",
        applies_to: {},
      },
    ]);
  const rm = (id) => onChange(rules.filter((r) => r.id !== id));
  const ch = (id, patch) => onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div className="space-y-4">
      {rules.map((r, idx) => (
        <div key={r.id} className="rounded border p-3 space-y-3">
          <div className="flex items-center justify-between">
            <input
              className={clsx("rounded border-gray-300 px-2 py-1", clsErr(errors, `booking_rules${idx}.label`))}
              placeholder="Label (optional)"
              value={r.label || ""}
              onChange={(e) => ch(r.id, { label: e.target.value })}
            />
            <button onClick={() => rm(r.id)} className="text-xs text-red-600 hover:underline">
              Remove
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <label className="text-sm">
              Booking cutoff (minutes) <Required />
              <input
                type="number"
                min="0"
                className={clsx("mt-1 block w-full rounded border-gray-300 px-2 py-1", clsErr(errors, `booking_rules${idx}.cutoff_min`))}
                value={r.cutoff_min ?? 0}
                onChange={(e) => ch(r.id, { cutoff_min: Math.max(0, Number(e.target.value || 0)) })}
              />
            </label>
            <label className="text-sm">
              Booking lead time (days) <Required />
              <input
                type="number"
                min="0"
                className={clsx("mt-1 block w-full rounded border-gray-300 px-2 py-1", clsErr(errors, `booking_rules${idx}.lead_days`))}
                value={r.lead_days ?? 0}
                onChange={(e) => ch(r.id, { lead_days: Math.max(0, Number(e.target.value || 0)) })}
              />
            </label>
            <label className="text-sm">
              Scope
              <select
                className={clsx("mt-1 block w-full rounded border-gray-300 px-2 py-1")}
                value={r.scope}
                onChange={(e) => ch(r.id, { scope: e.target.value })}
              >
                <option value="all">All Offerings</option>
                <option value="custom">Custom Selection</option>
              </select>
            </label>
          </div>

          {r.scope === "custom" && (
            <div className={clsx("rounded border p-3", clsErr(errors, `booking_rules${idx}.scope`))}>
              {Object.entries(modeItems).map(([mode, items]) =>
                items.length === 0 ? null : (
                  <div key={mode} className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-semibold">{mode.replace("_", " ")}</h4>
                      <button
                        type="button"
                        onClick={() =>
                          ch(r.id, {
                            applies_to: {
                              ...(r.applies_to || {}),
                              [mode]: items.map((it) => it.id),
                            },
                          })
                        }
                        className="text-xs text-blue-600 hover:underline"
                      >
                        Select All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {items.map(({ id, name }, i) => {
                        const sel = r.applies_to?.[mode] || [];
                        const checked = sel.includes(id);
                        return (
                          <label key={id} className="text-xs flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => {
                                const next = checked ? sel.filter((x) => x !== id) : [...sel, id];
                                ch(r.id, {
                                  applies_to: {
                                    ...(r.applies_to || {}),
                                    [mode]: next,
                                  },
                                });
                              }}
                            />
                            {name || `Item ${i + 1}`}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      ))}

      <button onClick={add} className="text-xs text-blue-600 hover:underline">
        + Add rule
      </button>
    </div>
  );
}